import { Units } from '../../services/api.types'

/**
 * Utility functions for formatting distance and speed based on user units
 * Handles conversion between metric (km) and imperial (miles) units
 */

// Conversion constants
const KM_TO_MILES = 0.621371
const MILES_TO_KM = 1.60934

/**
 * Converts kilometers to miles
 */
export const kmToMiles = (km: number): number => {
  return km * KM_TO_MILES
}

/**
 * Converts miles to kilometers
 */
export const milesToKm = (miles: number): number => {
  return miles * MILES_TO_KM
}

/**
 * Converts meters per second to miles per hour
 */
export const mpsToMph = (mps: number): number => {
  return mps * 2.23694
}

/**
 * Converts meters per second to kilometers per hour
 */
export const mpsToKph = (mps: number): number => {
  return mps * 3.6
}

/**
 * Formats distance based on user units
 * @param distanceInKm - Distance in kilometers
 * @param userUnits - User's preferred units (METRIC or IMPERIAL)
 * @returns Formatted distance string with appropriate unit
 */
export const formatDistance = (distanceInKm: number | null, userUnits: Units = Units.METRIC): string => {
  if (!distanceInKm || distanceInKm === 0) return '-'

  if (userUnits === Units.IMPERIAL) {
    return kmToMiles(distanceInKm).toFixed(2)
  } else {
    return distanceInKm.toFixed(2)
  }
}

/**
 * Formats distance for swimming activities (always in meters)
 * @param distanceInKm - Distance in kilometers
 * @returns Formatted distance string in meters
 */
export const formatSwimmingDistance = (distanceInKm: number | null): string => {
  if (!distanceInKm || distanceInKm === 0) return '-'

  // Distance in meters is shown without decimals (e.g. 1303 m, not 1303.20 m).
  return `${Math.round(distanceInKm * 1000)}`
}

/** Unit label for swimming distance (always meters). */
export const getSwimmingDistanceUnit = (): string => 'm'

/**
 * Formats speed based on user units
 * @param speedInMps - Speed in meters per second
 * @param userUnits - User's preferred units (METRIC or IMPERIAL)
 * @returns Formatted speed string with appropriate unit
 */
export const formatSpeed = (speedInMps: number, userUnits: Units = Units.METRIC): string => {
  if (!speedInMps || speedInMps === 0) return '0.00'

  if (userUnits === Units.IMPERIAL) {
    const mph = mpsToMph(speedInMps)
    return `${mph.toFixed(2)}`
  } else {
    const kph = mpsToKph(speedInMps)
    return `${kph.toFixed(2)}`
  }
}

/**
 * Formats pace based on user units
 * @param distanceInKm - Distance in kilometers
 * @param durationInSeconds - Duration in seconds
 * @param userUnits - User's preferred units (METRIC or IMPERIAL)
 * @returns Formatted pace string with appropriate unit
 */
export const formatPace = (
  distanceInKm: number | null,
  durationInSeconds: number | null,
  userUnits: Units = Units.METRIC,
): string => {
  if (!distanceInKm || !durationInSeconds || distanceInKm === 0 || durationInSeconds === 0) {
    return '-'
  }

  // Calculate seconds per unit
  const secondsPerUnit =
    userUnits === Units.IMPERIAL
      ? durationInSeconds / kmToMiles(distanceInKm) // seconds per mile
      : durationInSeconds / distanceInKm // seconds per km

  // Convert to minutes and seconds
  const minutes = Math.floor(secondsPerUnit / 60)
  const seconds = Math.floor(secondsPerUnit % 60)

  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

/**
 * Formats swimming pace (always per 100 meters)
 * @param distanceInKm - Distance in kilometers
 * @param durationInSeconds - Duration in seconds
 * @returns Formatted pace string per 100 meters
 */
export const formatSwimmingPace = (distanceInKm: number | null, durationInSeconds: number | null): string => {
  if (!distanceInKm || !durationInSeconds || distanceInKm === 0 || durationInSeconds === 0) {
    return '-'
  }

  // Convert distance to meters
  const distanceInMeters = distanceInKm * 1000

  // Calculate seconds per 100 meters
  const secondsPer100m = (durationInSeconds / distanceInMeters) * 100

  // Convert to minutes and seconds
  const minutes = Math.floor(secondsPer100m / 60)
  const seconds = Math.floor(secondsPer100m % 60)

  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

/**
 * Gets the appropriate unit label for distance based on user units
 * @param userUnits - User's preferred units
 * @returns Unit label string
 */
export const getDistanceUnit = (userUnits: Units): string => {
  return userUnits === Units.IMPERIAL ? 'mi' : 'km'
}

/**
 * Gets the appropriate unit label for speed based on user units
 * @param userUnits - User's preferred units
 * @returns Unit label string
 */
export const getSpeedUnit = (userUnits: Units): string => {
  return userUnits === Units.IMPERIAL ? 'mi/h' : 'km/h'
}

/**
 * Gets the appropriate unit label for pace based on user units
 * @param userUnits - User's preferred units
 * @returns Unit label string
 */
export const getPaceUnit = (userUnits: Units): string => {
  return userUnits === Units.IMPERIAL ? '/mi' : '/km'
}

/** Unit label for swimming pace (always per 100 meters). */
export const getSwimmingPaceUnit = (): string => '/100m'

const METERS_TO_FEET = 3.28084

/**
 * Converts meters to feet
 */
export const metersToFeet = (meters: number): number => {
  return meters * METERS_TO_FEET
}

/**
 * Formats an elevation value based on user units
 * @param meters - Elevation in meters
 * @param userUnits - User's preferred units (METRIC or IMPERIAL)
 * @returns Formatted elevation string, no decimals (e.g. "142")
 */
export const formatElevation = (meters: number | null, userUnits: Units = Units.METRIC): string => {
  if (!meters || meters === 0) return '-'

  return userUnits === Units.IMPERIAL ? `${Math.round(metersToFeet(meters))}` : `${Math.round(meters)}`
}

/**
 * Gets the appropriate unit label for elevation based on user units
 * @param userUnits - User's preferred units
 * @returns Unit label string
 */
export const getElevationUnit = (userUnits: Units): string => {
  return userUnits === Units.IMPERIAL ? 'ft' : 'm'
}

/**
 * Converts degrees Celsius to degrees Fahrenheit
 */
export const celsiusToFahrenheit = (celsius: number): number => {
  return (celsius * 9) / 5 + 32
}

/**
 * Gets the appropriate unit label for air temperature based on user units
 * @param userUnits - User's preferred units
 * @returns Unit label string
 */
export const getTemperatureUnit = (userUnits: Units): string => {
  return userUnits === Units.IMPERIAL ? '°F' : '°C'
}
